import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../lib/api'

/**
 * Avertismentul de expirare a modulelor ExpertAccounts.
 *
 * De ce există: ne-au expirat module fără ca nimeni să știe. Cei de la EA spun că trimit
 * notificări pe email; nici Victor, nici Alexandru nu au găsit vreuna. Deci starea o citim
 * din pagina lor de module, iar avertismentul îl dăm noi — mare, pe toată lățimea, la
 * fiecare intrare în CRM, până când cineva confirmă că modulul a fost reactivat.
 */
interface AlertModule {
  name: string
  status: 'activa' | 'expirata' | 'trial_expirat'
  validUntil: string | null
  expiredSince: string | null
  pricePerYear: number | null
  zileRamase: number | null
  /** Ce face CRM-ul cu modulul; la cele folosite nu există „Nu e necesar". */
  folositPentru: string | null
}
/** O funcție pe care EA o refuză acum pentru licență expirată. */
interface Refuz {
  endpoint: string
  addon: string | null
  functie: string | null
  dinLa: string
}
interface AlertResp {
  modules: AlertModule[]
  refuzuri?: Refuz[]
  pragZile: number
  syncedAt: string | null
  vechimeZile: number | null
}

const dmy = (iso: string | null) => (iso ? iso.slice(0, 10).split('-').reverse().join('.') : '—')

export function EaLicenseAlert() {
  const queryClient = useQueryClient()
  // Închiderea e doar pentru sesiunea curentă: fără confirmare, avertismentul revine la
  // următoarea intrare în CRM. Asta a fost cererea — să nu poată fi ignorat pe termen lung.
  const [inchisAcum, setInchisAcum] = useState(false)

  const q = useQuery<AlertResp>({
    queryKey: ['ea', 'module-licenses', 'alert'],
    queryFn: async () => (await api.get('/ea/module-licenses/alert')).data,
    // O dată pe sesiune e destul: datele se schimbă când cineva lipește pagina din EA.
    staleTime: 30 * 60 * 1000,
  })

  const nuMaiTrebuieMut = useMutation({
    mutationFn: async (name: string) =>
      (await api.post('/ea/module-licenses/not-needed', { name, value: true })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ea', 'module-licenses'] })
      toast.success('Marcat „nu e necesar" — nu mai apare')
    },
    onError: (e: unknown) => toast.error((e as Error).message || 'Eroare'),
  })

  const ackMut = useMutation({
    mutationFn: async (name: string) => (await api.post('/ea/module-licenses/ack', { name })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ea', 'module-licenses'] })
      toast.success('Confirmat — nu mai apare pentru această dată')
    },
    onError: (e: unknown) => toast.error((e as Error).message || 'Eroare'),
  })

  const modules = q.data?.modules ?? []
  const refuzuri = q.data?.refuzuri ?? []
  if (inchisAcum || (modules.length === 0 && refuzuri.length === 0)) return null

  /*
    Expirat e si un modul „activ" cu data trecuta: cardul il tine activ pana la urmatoarea lipire
    a paginii din EA, iar fereastra scria „Expira pe 12.09.2026 — in -23 zile".
  */
  const eExpirat = (m: AlertModule) =>
    m.status === 'expirata' || (m.zileRamase != null && m.zileRamase < 0)
  const expirate = modules.filter(eExpirat)
  const total = modules.reduce((s, m) => s + (m.pricePerYear ?? 0), 0)

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-auto bg-black/50 p-4 md:p-8">
      <div className="w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-2xl">
        <div
          className={`px-6 py-4 text-white ${expirate.length || refuzuri.length ? 'bg-red-600' : 'bg-amber-500'}`}
        >
          <div className="text-2xl font-bold">
            {refuzuri.length > 0
              ? 'ExpertAccounts refuză funcții folosite de CRM'
              : expirate.length > 0
                ? `${expirate.length} ${expirate.length === 1 ? 'modul ExpertAccounts a expirat' : 'module ExpertAccounts au expirat'}`
                : 'Module ExpertAccounts expiră în curând'}
          </div>
          <div className="mt-0.5 text-sm opacity-90">
            {modules.length === 1
              ? 'Un modul are nevoie de atenție.'
              : `${modules.length} module au nevoie de atenție.`}{' '}
            Confirmă după ce reactivezi licența — altfel mesajul apare la fiecare intrare în CRM.
            {q.data?.pragZile != null &&
              ` Avertizarea pornește cu ${q.data.pragZile} zile înainte.`}
          </div>
        </div>

        {/*
          REFUZURILE, primele: nu sunt o prognoză din card, ci un apel care tocmai a fost respins.
          Apar oricare ar fi starea cardului — și cu „Nu e necesar", și cu „Am reactivat" —, pentru
          că exact așa au trecut neobservate stocul și încasările în septembrie 2026.
        */}
        {refuzuri.length > 0 && (
          <div className="divide-y divide-red-100 border-b border-red-200 bg-red-50">
            {refuzuri.map((r) => (
              <div key={r.endpoint} className="px-6 py-3">
                <div className="font-semibold text-red-800">{r.addon ?? r.endpoint}</div>
                <div className="text-sm text-red-700">
                  EA răspunde „license expired” din {dmy(r.dinLa)}
                  {r.functie ? `. Nu merge: ${r.functie}.` : '.'}
                </div>
              </div>
            ))}
            <div className="px-6 py-2 text-xs text-red-700">
              Mesajul dispare singur la trei zile după ultimul refuz, adică după ce licența e
              reactivată și funcția răspunde din nou.
            </div>
          </div>
        )}

        <div className="divide-y divide-slate-100">
          {modules.map((m) => {
            const expirat = eExpirat(m)
            return (
              <div key={m.name} className="flex flex-wrap items-center gap-3 px-6 py-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-800">{m.name}</div>
                  {m.folositPentru && (
                    <div className="text-xs text-slate-500">Folosit de CRM: {m.folositPentru}</div>
                  )}
                  <div className="text-sm">
                    {expirat ? (
                      <span className="font-medium text-red-700">
                        Expirat din {dmy(m.expiredSince ?? m.validUntil)}
                        {m.zileRamase != null && ` — de ${Math.abs(m.zileRamase)} zile`}
                      </span>
                    ) : (
                      <span className="font-medium text-amber-700">
                        Expiră pe {dmy(m.validUntil)}
                        {m.zileRamase != null &&
                          ` — ${m.zileRamase === 0 ? 'astăzi' : `în ${m.zileRamase} ${m.zileRamase === 1 ? 'zi' : 'zile'}`}`}
                      </span>
                    )}
                    {m.pricePerYear != null && (
                      <span className="text-slate-500">
                        {' '}
                        · reînnoire {m.pricePerYear.toLocaleString('ro-RO')} RON/an
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {/* La un modul folosit de CRM nu se poate stinge avertismentul — serverul refuză. */}
                  {m.folositPentru == null && (
                    <button
                      type="button"
                      onClick={() => nuMaiTrebuieMut.mutate(m.name)}
                      disabled={nuMaiTrebuieMut.isPending}
                      className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                      title="Modulul rămâne în ExpertAccounts, dar nu mai apare aici"
                    >
                      Nu e necesar
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => ackMut.mutate(m.name)}
                    disabled={ackMut.isPending}
                    className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white hover:bg-slate-700 disabled:opacity-50"
                  >
                    Confirmă reactivarea
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 px-6 py-3 text-xs text-slate-500">
          <span>
            Reînnoirea se face din ExpertAccounts → Configurări → Module suplimentare.
            {total > 0 && ` Total afișat: ${total.toLocaleString('ro-RO')} RON/an.`}
            {q.data?.vechimeZile != null && q.data.vechimeZile > 90 && (
              <span className="text-amber-700">
                {' '}
                Starea e veche de {q.data.vechimeZile} zile — actualizeaz-o din Setări →
                ExpertAccounts.
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={() => setInchisAcum(true)}
            className="text-slate-500 underline hover:text-slate-700"
          >
            Închide până la următoarea intrare
          </button>
        </div>
      </div>
    </div>
  )
}
